import React from 'react';
import cx from 'classnames';

import { MDText } from 'i18n-react';

import {
  NoMoneyIcon,
  PaymentSpeedIcon,
  VehicleLorryIcon,
  VerifiedIcon,
} from '@motorway/mw-highway-code';

import type { TabItem } from 'Components/Tabs/Tabs';
import { Tabs } from 'Components/Tabs/Tabs';
import type { ServiceFeeTiersResponse } from 'Components/transactionFees/hooks/useServiceFeeData';

import { formatFee, formatPriceRange } from '../helpers/formatTier';
import { AgentIcon } from '../icons/AgentIcon';
import { OkIcon } from '../icons/OkIcon';
import { PaymentSentIcon } from '../icons/PaymentSentIcon';
import Texts from '../transactionFees.texts.json';

import styles from './ServiceFeeExplainer.scss';

const LocalT = new MDText(Texts);

const CONDITIONS = [
  { icon: NoMoneyIcon, key: 'noSaleNoFee' },
  { icon: PaymentSpeedIcon, key: 'fastPayment' },
  { icon: VehicleLorryIcon, key: 'freeCollection' },
  { icon: VerifiedIcon, key: 'verifiedDealer' },
  { icon: OkIcon, key: 'financeSettlement' },
  { icon: PaymentSentIcon, key: 'refundPolicy' },
  { icon: AgentIcon, key: 'customerService' },
] as const;

type ServiceFeeExplainerProps = {
  serviceFeeTiers?: ServiceFeeTiersResponse;
};

export const ServiceFeeExplainer: React.FC<ServiceFeeExplainerProps> = ({ serviceFeeTiers }) => {
  const tiers = serviceFeeTiers?.tiers ?? [];

  const conditionsPanel = (
    <div className={styles.conditionList}>
      {CONDITIONS.map(({ icon: Icon, key }) => (
        <div key={key} className={styles.conditionItem}>
          <div className={styles.conditionIcon}>
            <Icon filled={false} size="medium" />
          </div>
          <div className={styles.conditionContent}>
            <span className={styles.conditionTitle}>
              {LocalT.translate(`serviceFeeExplainer.conditions.${key}.title`)}
            </span>
            <span className={styles.conditionDescription}>
              {LocalT.translate(`serviceFeeExplainer.conditions.${key}.description`)}
            </span>
          </div>
        </div>
      ))}
    </div>
  );

  const tierPanel = (
    <>
      <span className={styles.tierDescription}>{LocalT.translate('serviceFeeExplainer.tierTableSection.description') as string}</span>
      <section className={styles.tierTable} data-testid="service-fee-tier-table">
        <div key={'header'} className={cx(styles.tierRow, styles.tierHeader)} data-testid='tier-row-header'>
          <span>{LocalT.translate('serviceFeeExplainer.tierTable.header') as string}</span>
          <span>{LocalT.translate('serviceFeeExplainer.tierTable.value') as string}</span>
        </div>
        {tiers?.map((tier, index) => {
          const priceRange = formatPriceRange({
            index,
            isLast: index === tiers.length - 1,
            maxPrice: tier.maxPrice,
            minPrice: tier.minPrice,
          });
          const fee = formatFee(
            tier.fee,
            LocalT.translate('serviceFeeExplainer.tierTable.free') as string,
          );
          return (
            <section key={tier.tier}>
              { index !== 0 && (<div className={styles.spacer} />) }
              <div className={styles.tierRow} data-testid={`tier-row-${tier.tier}`}>
                <span className={styles.tierCell}>{priceRange}</span>
                <span className={styles.tierCell}>{fee}</span>
              </div>
            </section>
          );
        })}
      </section>
    </>
  );

  const tabItems: TabItem[] = [
    {
      label: LocalT.translate('serviceFeeExplainer.tabs.whatsIncluded') as string,
      panel: conditionsPanel,
    },
    ...(tiers?.length > 0 ? [{
      label: LocalT.translate('serviceFeeExplainer.tabs.howFeeCalculated') as string,
      panel: tierPanel,
    }] : []),
  ];

  return (
    <div className={styles.explainer} data-testid="transaction-fee-explainer">
      <p className={styles.heading}>
        {LocalT.translate('serviceFeeExplainer.heading')}
      </p>
      <Tabs
        className={styles.tabsWrapper}
        idPrefix="service-fee"
        tabs={tabItems}
      />
    </div>
  );
};
